import { useState, useEffect } from "react";
import { ArrowUpRight, Clock } from "lucide-react";
import axios from "axios";
import API from "@/lib/api";

const EXTERNAL = "https://ngxptech.com/";

export default function Blogs() {
  const [blogPosts, setBlogPosts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/blogs`)
      .then(({ data }) =>
        setBlogPosts(
          (Array.isArray(data) ? data : []).map((p) => ({ ...p, image: p.image_url }))
        )
      )
      .catch(console.error);
  }, []);

  return (
    <div data-testid="blogs-page">
      {/* HERO */}
      <section className="relative bg-white pt-6 md:pt-10 pb-12 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-pattern" />
        <div className="glow-blob bg-[#FF007F]/15 w-[500px] h-[500px] -top-32 -right-20 pulse-glow" />
        <div className="container-ngxp relative z-10 max-w-4xl">
          <span className="overline">Insights</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[#0A0B10]">
            Ideas, perspectives and engineering notes on <span className="gradient-text-glow">intelligent operations.</span>
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-2xl">
            Selected reading from the NGXP team on AI-native enterprise systems, digital twins, agentic AI, IoT, edge intelligence, immersive XR training and command intelligence.
          </p>
        </div>
      </section>

      {/* BLOG GRID */}
      <section className="bg-[#F8F9FA] py-14 md:py-20">
        <div className="container-ngxp">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, i) => (
              <a
                key={i}
                href={EXTERNAL}
                target="_blank"
                rel="noreferrer"
                className="group block ngxp-card !p-0 overflow-hidden reveal"
                data-testid={`blog-card-${i}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute top-4 left-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/95 backdrop-blur text-xs font-semibold text-[#0A0B10] border border-white/40">
                      <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#7B2CBF] to-[#FF007F]" />
                      {post.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
                    <Clock className="w-3 h-3" />
                    <span>{post.read_time ? `${post.read_time} min read` : "5 min read"}</span>
                  </div>
                  <h3 className="font-display text-lg font-semibold leading-snug text-[#0A0B10] group-hover:gradient-text-glow transition-colors">
                    {post.title}
                  </h3>
                  <p className="mt-3 text-sm text-gray-600 leading-relaxed line-clamp-3">{post.summary}</p>
                  <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#7B2CBF] group-hover:gap-2 transition-all" data-testid={`blog-${i}-read-more`}>
                    Read More <ArrowUpRight className="w-4 h-4" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-14 md:py-18">
        <div className="container-ngxp">
          <div className="relative rounded-3xl bg-gradient-to-br from-[#00A8E8] via-[#7B2CBF] to-[#FF007F] p-10 md:p-16 text-white overflow-hidden">
            <div className="absolute inset-0 dark-grid-pattern opacity-30" />
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 items-center gap-6">
              <div className="lg:col-span-8">
                <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Read more on ngxptech.com</h3>
                <p className="mt-3 text-white/85 max-w-xl">Full collection of articles, case notes and engineering perspectives from the NGXP team.</p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <a
                  href={EXTERNAL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="blogs-external-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-white text-[#0A0B10] px-8 py-4 text-sm font-semibold hover:-translate-y-0.5 transition-transform"
                >
                  Visit Blog Site <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
